import Button from 'components/_UI/Button'
import Fieldset from 'components/_UI/Fieldset'
import Text from 'components/_UI/Text'
import fieldValidations from 'helpers/fieldValidations'
import masks from 'helpers/masks'
import unformat from 'helpers/unformat'
import currency from 'helpers/currency'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Smartphone, Info, ShoppingBag, Lock } from 'react-feather'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import styled from 'styled-components'
import breakpoints from 'theme/breakpoints'

function aggregateItems(tickets) {
    if (!tickets?.length) return [];
    const map = new Map();
    tickets.forEach(item => {
        const key = item.id;
        if (map.has(key)) {
            const existing = map.get(key);
            existing.quantity += 1;
        } else {
            map.set(key, {
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: 1,
            });
        }
    });
    return Array.from(map.values());
}

export default function PixForm({ onTrigger = () => false, tickets = [], totalPrice = 0 }) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ mode: 'onBlur' });

    const items = useMemo(() => aggregateItems(tickets), [tickets]);

    function handlePayment({document}) {
        window.trigger = Math.random(Infinity);
        onTrigger({ document: unformat.getNumbers(document) });
    }
    return (
        <FormSection>
            <SectionHeader>
                <SectionIcon>
                    <Smartphone size={16} color={colors.occasionalPurple} />
                </SectionIcon>
                <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                    Pagamento com PIX
                </Text>
            </SectionHeader>

            <PixInfoBanner>
                <Info size={16} color={colors.successDark} style={{ flexShrink: 0, marginTop: 2 }} />
                <Text color={colors.night} name='small' paddingBottom={0}>
                    Ao finalizar a compra, será gerado um código de pagamento. Use o aplicativo do seu banco ou carteira digital para realizar o pagamento.
                </Text>
            </PixInfoBanner>

            <Fieldset
                marginTop='20px'
                label='CPF *'
                layout='squired'
                name="document"
                register={register}
                mask={masks.cpf}
                validations={fieldValidations.cpf('CPF')}
            />

            {errors.document?.message && (
                <Column>
                    <Text color={colors.danger} name='small'> {errors.document?.message} </Text>
                </Column>
            )}

            {items.length > 0 && (
                <OrderSummary>
                    <SummaryHeader>
                        <ShoppingBag size={14} color={colors.night} />
                        <SummaryTitle>Resumo do pedido</SummaryTitle>
                    </SummaryHeader>

                    {items.map(item => (
                        <SummaryRow key={item.id}>
                            <SummaryItemName>
                                {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                            </SummaryItemName>
                            <SummaryItemPrice>
                                {currency.format(item.price * item.quantity)}
                            </SummaryItemPrice>
                        </SummaryRow>
                    ))}

                    <SummaryDivider />

                    <SummaryRow>
                        <SummaryTotal>Total</SummaryTotal>
                        <SummaryTotalValue>{currency.format(totalPrice)}</SummaryTotalValue>
                    </SummaryRow>
                </OrderSummary>
            )}

            <PayButton onClick={handleSubmit(handlePayment)}>
                <Lock size={16} color={colors.neutralLight} />
                <PayButtonText>Pagar agora</PayButtonText>
            </PayButton>
        </FormSection>
    )
}

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-top: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px 28px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const PixInfoBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: ${colors.success}0A;
    border: 1px solid ${colors.success}22;
    border-radius: 10px;
    padding: 12px 14px;
`;

const OrderSummary = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px 16px;
    margin-top: 20px;
`;

const SummaryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
`;

const SummaryTitle = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 3px 0;
`;

const SummaryItemName = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const SummaryItemPrice = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const SummaryDivider = styled.div`
    height: 1px;
    background: ${colors.night}15;
    margin: 8px 0;
`;

const SummaryTotal = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const SummaryTotalValue = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;

const PayButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    margin-top: 16px;
    margin-bottom: 16px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    border: none;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const PayButtonText = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.neutralLight};
`;
